"use client";

import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useEffect,
    useCallback,
} from "react";
import { FILTER_DEFAULTS } from "./filterDefaults";

interface GeoLocation {
    latitude: number;
    longitude: number;
}

interface EventFilterContextProps {
    searchTerm: string;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    pastEvents: boolean;
    setPastEvents: React.Dispatch<React.SetStateAction<boolean>>;
    geoLocation: GeoLocation | undefined;
    setGeoLocation: React.Dispatch<
        React.SetStateAction<GeoLocation | undefined>
    >;
    onlyShowCurrentUserAttendingEvents: boolean;
    setOnlyShowCurrentUserAttendingEvents: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    communityFilter: string;
    setCommunityFilter: React.Dispatch<React.SetStateAction<string>>;
    startOfCurrentDay: Date;
    setStartOfCurrentDay: React.Dispatch<React.SetStateAction<Date>>;
    includeAttendeesNames: boolean;
    setIncludeAttendeesNames: React.Dispatch<React.SetStateAction<boolean>>;
    includeHostNames: boolean;
    setIncludeHostNames: React.Dispatch<React.SetStateAction<boolean>>;
    debouncedSetSearchTerm: (value: string) => void;
    debouncedSearchTerm: string;
    onlyShowCurrentUserHostingEvents: boolean;
    setOnlyShowCurrentUserHostingEvents: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    userId: string | undefined;
    setUserId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const EventFilterContext = createContext<EventFilterContextProps | undefined>(
    undefined,
);

interface EventFilterProviderProps {
    children: ReactNode;
}

export const useEventFilter = () => {
    const [searchTerm, setSearchTerm] = useState(FILTER_DEFAULTS.searchTerm);
    const [pastEvents, setPastEvents] = useState(FILTER_DEFAULTS.pastEvents);
    const [geoLocation, setGeoLocation] = useState<GeoLocation>();
    const [
        onlyShowCurrentUserAttendingEvents,
        setOnlyShowCurrentUserAttendingEvents,
    ] = useState(FILTER_DEFAULTS.onlyShowCurrentUserAttendingEvents);
    const [communityFilter, setCommunityFilter] = useState("");
    const [startOfCurrentDay, setStartOfCurrentDay] = useState(
        FILTER_DEFAULTS.startOfCurrentDay,
    );
    const [includeAttendeesNames, setIncludeAttendeesNames] = useState(
        FILTER_DEFAULTS.includeAttendeesNames,
    );
    const [includeHostNames, setIncludeHostNames] = useState(
        FILTER_DEFAULTS.includeHostNames,
    );
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [
        onlyShowCurrentUserHostingEvents,
        setOnlyShowCurrentUserHostingEvents,
    ] = useState(FILTER_DEFAULTS.onlyShowCurrentUserHostingEvents);
    const [userId, setUserId] = useState<string | undefined>();

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => {
            clearTimeout(debounceTimeout);
        };
    }, [searchTerm]);

    const debouncedSetSearchTerm = useCallback((value: string) => {
        setSearchTerm(value);
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        pastEvents,
        setPastEvents,
        geoLocation,
        setGeoLocation,
        onlyShowCurrentUserAttendingEvents,
        setOnlyShowCurrentUserAttendingEvents,
        communityFilter,
        setCommunityFilter,
        startOfCurrentDay,
        setStartOfCurrentDay,
        includeAttendeesNames,
        setIncludeAttendeesNames,
        includeHostNames,
        setIncludeHostNames,
        debouncedSetSearchTerm,
        debouncedSearchTerm,
        onlyShowCurrentUserHostingEvents,
        setOnlyShowCurrentUserHostingEvents,
        userId,
        setUserId,
    };
};

export const EventFilterProvider: React.FC<EventFilterProviderProps> = ({
    children,
}) => {
    const contextValues = useEventFilter();

    return (
        <EventFilterContext.Provider value={contextValues}>
            {children}
        </EventFilterContext.Provider>
    );
};

export const useEventFilterContext = () => {
    const context = useContext(EventFilterContext);
    if (context === undefined) {
        throw new Error(
            "useEventFilterContext must be used within an EventFilterProvider",
        );
    }
    return context;
};
